/** Max forms we preload in one import run (catalog size guard). */
export const FORM_IMPORT_PRELOAD_MAX_COUNT = 1000;

const FORM_LIST_PAGE_SIZE = 100;

const collapseWhitespace = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

/** Case-insensitive exact key: collapse whitespace, trim, uppercase. */
export const normalizeExactFormTitleKey = (title) => collapseWhitespace(title).toUpperCase();

/**
 * Looser match key: collapse whitespace, lowercase.
 * Used when exact case-insensitive title is not found.
 */
export const normalizeLooseFormTitleKey = (title) => collapseWhitespace(title).toLowerCase();

const resolveFormTitleKeyFromCell = (formTitleCell) => {
    if (formTitleCell == null) {
        return "";
    }
    if (typeof formTitleCell === "string") {
        return collapseWhitespace(formTitleCell);
    }
    return collapseWhitespace(formTitleCell?.title);
};

/**
 * In-memory form resolution for one import run.
 * Match order: exact case-insensitive title, then unique loose-insensitive title, then catalog scan.
 */
export class FormTitleLookupStore {
    constructor() {
        /** @type {Array<{ id: string, title?: string }>} */
        this._forms = [];
        /** @type {{ byExactKey: Map<string, *>, byLooseKey: Map<string, *> } | null} */
        this._indexes = null;
    }

    clear() {
        this._forms = [];
        this._indexes = null;
    }

    /**
     * @param {Array<{ id: string, title?: string }>} forms
     */
    setCatalog(forms) {
        this._forms = forms ?? [];
        const byExactKey = new Map();
        const byLooseKey = new Map();

        for (const form of this._forms) {
            const title = collapseWhitespace(form?.title);
            if (!title || form?.id == null || form.id === "") {
                continue;
            }
            const exactKey = normalizeExactFormTitleKey(title);
            if (exactKey && !byExactKey.has(exactKey)) {
                byExactKey.set(exactKey, form);
            }
            const looseKey = normalizeLooseFormTitleKey(title);
            if (looseKey) {
                const existing = byLooseKey.get(looseKey);
                if (!existing) {
                    byLooseKey.set(looseKey, form);
                } else if (existing.id !== form.id) {
                    byLooseKey.set(looseKey, null);
                }
            }
        }

        this._indexes = {byExactKey, byLooseKey};
    }

    /**
     * @param {Array<{ id: string, title?: string }>} [preloadedForms]
     * @param {*} [formsApi]
     */
    async ensureCatalog(preloadedForms, formsApi) {
        if (preloadedForms?.length) {
            this.setCatalog(preloadedForms);
            return;
        }

        if (this._forms.length > 0) {
            return;
        }

        if (formsApi) {
            const forms = await fetchAllFormsFromApi(formsApi);
            this.setCatalog(forms);
        }
    }

    /**
     * @param {string} titleTrimmed
     * @returns {*|null}
     */
    #findFormByCatalogScan(titleTrimmed) {
        const exactKey = normalizeExactFormTitleKey(titleTrimmed);
        const looseKey = normalizeLooseFormTitleKey(titleTrimmed);

        const exactMatches = this._forms.filter(
            (form) => normalizeExactFormTitleKey(form?.title) === exactKey,
        );
        if (exactMatches.length === 1) {
            return exactMatches[0];
        }

        const looseMatches = this._forms.filter(
            (form) => normalizeLooseFormTitleKey(form?.title) === looseKey,
        );
        if (looseMatches.length === 1) {
            return looseMatches[0];
        }

        return null;
    }

    /**
     * @param {string} titleTrimmed
     * @returns {*|null}
     */
    resolveFormByTitle(titleTrimmed) {
        const normalizedTitle = collapseWhitespace(titleTrimmed);
        if (!normalizedTitle) {
            return null;
        }

        if (this._indexes) {
            const exactKey = normalizeExactFormTitleKey(normalizedTitle);
            if (exactKey && this._indexes.byExactKey.has(exactKey)) {
                const exactHit = this._indexes.byExactKey.get(exactKey);
                if (exactHit) {
                    return exactHit;
                }
            }

            const looseKey = normalizeLooseFormTitleKey(normalizedTitle);
            if (looseKey) {
                const looseHit = this._indexes.byLooseKey.get(looseKey);
                if (looseHit) {
                    return looseHit;
                }
            }
        }

        return this.#findFormByCatalogScan(normalizedTitle);
    }

    /**
     * @param {string[]} titlesTrimmed
     * @param {Array<{ id: string, title?: string }>} [preloadedForms]
     * @param {*} [formsApi]
     * @returns {Promise<{ formIds: Record<string, *>, formInfo: Record<string, *> }>}
     */
    async resolve(titlesTrimmed, preloadedForms, formsApi) {
        await this.ensureCatalog(preloadedForms, formsApi);

        /** @type {Record<string, *>} */
        const formIds = {};
        /** @type {Record<string, *>} */
        const formInfo = {};

        for (const raw of titlesTrimmed ?? []) {
            const trimmed =
                typeof raw === "string" ? collapseWhitespace(raw) : resolveFormTitleKeyFromCell(raw);
            if (!trimmed) {
                continue;
            }
            const lookupKey = normalizeExactFormTitleKey(trimmed);
            const form = this.resolveFormByTitle(trimmed);
            if (form?.id != null) {
                formIds[lookupKey] = form.id;
                formInfo[lookupKey] = form;
            }
        }

        return {formIds, formInfo};
    }
}

/**
 * @param {*} listFormsResponse
 * @returns {Array<{ id: string, title?: string }>}
 */
const extractListFormsItems = (listFormsResponse) => {
    const items = listFormsResponse?.data?.items;
    if (Array.isArray(items)) {
        return items;
    }
    return listFormsResponse?.resultFlag === false ? [] : [];
};

/**
 * Paginates operator form list until the catalog is fully loaded.
 *
 * @param {*} formsApi
 * @returns {Promise<Array<{ id: string, title?: string }>>}
 */
export async function fetchAllFormsFromApi(formsApi) {
    if (!formsApi) {
        return [];
    }

    const probe = await formsApi.listForms({
        keyword: "",
        pagination: {pageSize: 1, currentPage: 1},
        sorts: [],
    });
    const probeItems = extractListFormsItems(probe);
    const totalCount = probe?.data?.totalCount ?? probeItems.length ?? 0;
    if (totalCount <= 0) {
        return [];
    }
    if (totalCount > FORM_IMPORT_PRELOAD_MAX_COUNT) {
        console.warn(
            `Form import preload: totalCount ${totalCount} exceeds max ${FORM_IMPORT_PRELOAD_MAX_COUNT}; loading first ${FORM_IMPORT_PRELOAD_MAX_COUNT} only.`,
        );
    }

    const target = Math.min(totalCount, FORM_IMPORT_PRELOAD_MAX_COUNT);
    const items = [];
    let currentPage = 1;

    while (items.length < target) {
        const response = await formsApi.listForms({
            keyword: "",
            pagination: {pageSize: FORM_LIST_PAGE_SIZE, currentPage},
            sorts: [],
        });
        const pageItems = extractListFormsItems(response);
        if (!pageItems.length) {
            break;
        }
        items.push(...pageItems);
        currentPage += 1;
    }


    return items.slice(0, target);
}
